import React from "react";
import { Link } from "react-router-dom";
import { trackAction } from "@/content/site";

/**
 * [Phase 26] Slim live Google-rating strip — homepage social proof.
 *
 * Section payload is built server-side from the live Places snapshot, so
 * the figure always matches the AggregateRating JSON-LD. Absent entirely
 * when the feed is unavailable.
 */
export default function GoogleRatingStrip({ section, lang }) {
  if (!section || !section.rating || !section.count) return null;

  const fullStars = Math.round(section.rating);
  const ratingLabel =
    lang === "es"
      ? `${section.rating.toFixed(1)} de 5 estrellas`
      : `${section.rating.toFixed(1)} out of 5 stars`;

  const handleGoogleClick = () => {
    trackAction("google_reviews_click", {
      link_domain: "maps.google.com",
      location: "homepage_strip",
      review_count: section.count,
      rating_value: section.rating,
    });
  };

  return (
    <section className="gw-rating-strip" data-testid="google-rating-strip">
      <div className="gw-container gw-rating-strip-inner">
        <span
          className="gw-rating-strip-stars"
          role="img"
          aria-label={ratingLabel}
          data-testid="strip-rating-stars"
        >
          {"★".repeat(fullStars)}
          {"☆".repeat(Math.max(0, 5 - fullStars))}
        </span>
        <span className="gw-rating-strip-text" data-testid="strip-rating-text">
          <strong>{section.rating.toFixed(1)}</strong> · {section.text}
        </span>
        <span className="gw-rating-strip-links">
          <Link
            to={section.testimonials_href}
            className="gw-rating-strip-link"
            data-testid="strip-testimonials-link"
          >
            {section.testimonials_label}
          </Link>
          {section.google_maps_uri && (
            <a
              href={section.google_maps_uri}
              target="_blank"
              rel="noopener noreferrer"
              className="gw-rating-strip-link"
              onClick={handleGoogleClick}
              data-testid="strip-google-link"
            >
              {section.google_label}
            </a>
          )}
        </span>
      </div>

      <style>{`
        .gw-rating-strip {
          background: var(--gw-primary-dark, #3B0A14);
          border-top: 1px solid rgba(212, 160, 23, 0.35);
          border-bottom: 1px solid rgba(212, 160, 23, 0.35);
          padding: var(--gw-space-3) 0;
        }
        .gw-rating-strip-inner {
          display: flex; flex-wrap: wrap; align-items: center;
          justify-content: center; text-align: center;
          gap: var(--gw-space-2) var(--gw-space-5);
        }
        .gw-rating-strip-stars {
          color: var(--gw-accent); font-size: 1.05rem; letter-spacing: 2px;
          line-height: 1;
        }
        .gw-rating-strip-text {
          color: rgba(255, 255, 255, 0.92); font-size: 0.95rem;
        }
        .gw-rating-strip-text strong {
          font-family: var(--gw-font-serif); font-size: 1.05rem; color: #fff;
        }
        .gw-rating-strip-links {
          display: inline-flex; flex-wrap: wrap; gap: var(--gw-space-4);
          justify-content: center;
        }
        .gw-rating-strip-link {
          color: var(--gw-accent); font-weight: 600; font-size: 0.9rem;
          text-decoration: none; border-bottom: 1px solid transparent;
          transition: border-color 180ms ease, opacity 180ms ease;
          min-height: 44px; display: inline-flex; align-items: center;
        }
        .gw-rating-strip-link:hover,
        .gw-rating-strip-link:focus-visible {
          border-bottom-color: var(--gw-accent);
        }
      `}</style>
    </section>
  );
}
